import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const LegalTerms = () => {
  const currentYear = new Date()?.getFullYear();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-900 hover:text-accent transition-colors">
            <Icon name="HardHat" size={24} className="text-accent" />
            <span className="font-semibold text-lg">ConstructionPro</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/privacy-policy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" className="hover:text-gray-900 transition-colors">Cookie Policy</Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 md:p-12">
          {/* Title */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="FileText" size={20} className="text-accent" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Legal Terms</h1>
            </div>
            <p className="text-gray-500 text-sm">Effective date: January 1, 2025 &nbsp;&bull;&nbsp; Version 1.0</p>
          </div>

          <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
            {/* Agreement */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Agreement to Terms</h2>
              <p>
                By accessing or using the ConstructionPro platform, you agree to be bound by these Legal Terms and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this platform.
              </p>
            </section>

            {/* Use License */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Use License</h2>
              <p className="mb-3">
                Subject to your compliance with these Terms, ConstructionPro grants you a limited, non-exclusive, non-transferable, revocable license to access and use the platform for your internal business purposes.
              </p>
              <p className="mb-3">You may not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Modify, copy, or create derivative works based on the platform</li>
                <li>Reverse engineer or attempt to extract source code</li>
                <li>Sell, resell, or sublicense access to the platform</li>
                <li>Use the platform for any unlawful purpose or in violation of any regulations</li>
                <li>Transmit any viruses, malware, or other harmful code</li>
                <li>Attempt to gain unauthorized access to any portion of the platform</li>
              </ul>
            </section>

            {/* Account Responsibilities */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Account Responsibilities</h2>
              <p className="mb-3">You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Ensuring all users added to your organization comply with these Terms</li>
                <li>Promptly notifying us of any unauthorized use of your account</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Intellectual Property</h2>
              <p>
                The ConstructionPro platform, including its design, features, and content, is owned by ConstructionPro and protected by intellectual property laws. You retain ownership of all data and content you upload to the platform. By uploading content, you grant ConstructionPro a limited license to use that content solely to provide the platform services.
              </p>
            </section>

            {/* Disclaimer */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Disclaimer of Warranties</h2>
              <p>
                The platform is provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. ConstructionPro does not warrant that the platform will be uninterrupted, error-free, or free of viruses.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, ConstructionPro shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform. In no event shall our total liability to you exceed the greater of (a) the amount you paid us in the 12 months preceding the claim or (b) $100 USD.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Termination</h2>
              <p>
                We may terminate or suspend your access to the platform immediately, without prior notice, if you breach these Terms. Upon termination, your right to use the platform ceases immediately. Provisions that by their nature should survive termination will remain in effect.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in New York, New York.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will provide notice of significant changes by updating the effective date and, where appropriate, notifying you via email. Continued use of the platform after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Contact Us</h2>
              <p className="mb-2">For legal inquiries, please contact us:</p>
              <div className="space-y-1 text-sm">
                <p><strong>Email:</strong> <a href="mailto:legal@constructionpro.com" className="text-accent hover:underline">legal@constructionpro.com</a></p>
                <p><strong>Address:</strong> ConstructionPro, 123 Build Street, Suite 400, New York, NY 10001</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-8">
        <div className="max-w-4xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-500">
          <span>Copyright {currentYear} ConstructionPro. All rights reserved.</span>
          <nav className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/legal-terms" className="hover:text-gray-900 transition-colors font-medium text-accent">Legal</Link>
            <span className="text-gray-300">|</span>
            <Link to="/cookie-policy" className="hover:text-gray-900 transition-colors">Cookie Policy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default LegalTerms;
